/**
 * 图片上传组件 - 通用组件，支持预览和删除
 */

import React, { useState, useEffect } from 'react';
import { Upload, message, Modal } from 'antd';
import { UploadOutlined, EyeOutlined, DeleteOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import './index.css';

interface PictureUploadProps {
  value?: string;
  onChange?: (value: string) => void;
  maxCount?: number;
  beforeUpload?: (file: File) => Promise<boolean>;
  disabled?: boolean;
  accept?: string;
  listType?: 'text' | 'picture' | 'picture-card';
  previewTitle?: string;
}

const PictureUpload: React.FC<PictureUploadProps> = ({
  value,
  onChange,
  maxCount = 1,
  beforeUpload,
  disabled = false,
  accept = 'image/*',
  listType = 'picture-card',
  previewTitle = '图片预览',
}) => {
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  // 根据value同步fileList
  useEffect(() => {
    if (value && value.startsWith('data:image')) {
      // Base64图片
      setFileList([
        {
          uid: '-1',
          name: 'image.png',
          status: 'done',
          url: value,
        } as UploadFile,
      ]);
    } else if (value) {
      setFileList([
        {
          uid: '-1',
          name: 'image.png',
          status: 'done',
          url: value,
        } as UploadFile,
      ]);
    } else {
      setFileList([]);
    }
  }, [value]);

  // 默认的图片压缩和转换
  const defaultBeforeUpload = async (file: File): Promise<boolean> => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('只能上传图片文件！');
      return false;
    }

    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error('图片大小不能超过 2MB！');
      return false;
    }

    // 压缩图片并转换为Base64
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        const maxWidth = 800;
        const maxHeight = 600;
        let { width, height } = img;

        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;
        ctx!.drawImage(img, 0, 0, width, height);

        const base64 = canvas.toDataURL('image/jpeg', 0.8);
        const newFileList = [
          {
            uid: Date.now().toString(),
            name: file.name,
            status: 'done',
            url: base64,
          } as UploadFile,
        ];
        setFileList(newFileList);
        onChange?.(base64);
        resolve(true);
      };

      img.src = URL.createObjectURL(file);
    });
  };

  const handleChange = (info: any) => {
    if (info.file.status === 'removed') {
      setFileList([]);
      onChange?.('');
    } else if (info.file.status === 'done' && info.fileList.length > 0) {
      setFileList(info.fileList);
      const imageUrl = info.fileList[info.fileList.length - 1].url;
      onChange?.(imageUrl || '');
    }
  };

  const handlePreview = (file: UploadFile) => {
    const imageUrl = file.url || file.thumbUrl;
    if (imageUrl) {
      setPreviewImage(imageUrl);
      setPreviewVisible(true);
    }
  };

  const handleRemove = () => {
    const newFileList = fileList.slice(0, -1); // 移除最后一个文件
    setFileList(newFileList);
    onChange?.('');
    message.success('图片已删除');
  };

  return (
    <>
      <style>{`
        /* 隐藏文件名 */
        .custom-upload-item .ant-upload-list-item-name,
        .ant-upload-list-item-name,
        .ant-upload-list-item-name a,
        .ant-upload-list-item-name span,
        .ant-upload-list-item-name-link,
        .ant-upload-list-item-name-link span {
          display: none !important;
          visibility: hidden !important;
          height: 0 !important;
          width: 0 !important;
          margin: 0 !important;
          padding: 0 !important;
          font-size: 0 !important;
          overflow: hidden !important;
        }
        /* 移除悬停背景 */
        .ant-upload-list-item:hover {
          background-color: transparent !important;
          background: transparent !important;
        }
        .ant-upload-list-item:hover .ant-upload-list-item-info {
          background-color: transparent !important;
          background: transparent !important;
        }
        .ant-upload-list-item:hover .ant-upload-list-item-info::before {
          display: none !important;
          opacity: 0 !important;
          background: none !important;
        }
        .ant-upload-list-item:hover .ant-upload-list-item-thumbnail {
          background-color: transparent !important;
          background: transparent !important;
        }
      `}</style>
      <Upload
        listType={listType}
        maxCount={maxCount}
        disabled={disabled}
        accept={accept}
        fileList={fileList}
        onChange={handleChange}
        beforeUpload={async (file) => {
          if (beforeUpload) {
            return await beforeUpload(file);
          }
          await defaultBeforeUpload(file);
          return false; // 阻止自动上传
        }}
        showUploadList={{
          showPreviewIcon: false,
          showRemoveIcon: false,
          showDownloadIcon: false,
        }}
        itemRender={(originNode, file, fileList, actions) => {
          return (
            <div className="custom-upload-item">
              <div style={{ position: 'relative' }}>
                {originNode}
                <div className="upload-actions">
                  <span className="upload-action-btn" onClick={() => handlePreview(file)} title="查看">
                    <EyeOutlined />
                  </span>
                  <span className="upload-action-btn" onClick={handleRemove} title="删除">
                    <DeleteOutlined />
                  </span>
                </div>
              </div>
            </div>
          );
        }}
      >
        {fileList.length >= maxCount ? null : (
          <div>
            <UploadOutlined />
            <div style={{ marginTop: 8 }}>上传图片</div>
          </div>
        )}
      </Upload>

      <Modal
        title={previewTitle}
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={null}
        width={600}
        centered
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <img src={previewImage} alt="预览" style={{ maxWidth: '100%', height: 'auto' }} />
        </div>
      </Modal>
    </>
  );
};

export default PictureUpload;

